import { DestroyRef, ErrorHandler, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { FeatureFlag, NewPermission } from '@core/enums';
import { SavedView } from '@core/models';
import { AccountService } from '@core/services';
import { PermissionUtils } from '@core/utils/permission.utils';
import { Store } from '@ngxs/store';
import { SavedViewAddComponent } from '@shared/modules/saved-views/modals/saved-view-add/saved-view-add.component';
import { AccountState } from '@state/account/state';
import { SavedViewsState } from '@state/saved-view/state';
import { UserState } from '@state/user/state';
import { TaskActions } from '@tasks/state/actions';
import { TaskState } from '@tasks/state/state';
import { CreateTaskListComponent } from '@tasks/task-shell/create-task-list/create-task-list.component';
import { RelatedToType } from '@shared/enums';
import { SavedViewCreateParams, TaskRelatedToModule, TaskView, TaskViewKind } from '@tasks/tasks.types';
import { JupiterState } from 'app/modules/jupiter/@store/state';
import { catchError, EMPTY, exhaustMap, map, Observable, of, switchMap } from 'rxjs';
import { TsPageSidenavConfig } from 'tsui/lib/ts-page/ts-page.component';

@Injectable()
export class TaskSidebarConfigService {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private matDialog = inject(MatLegacyDialog);
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);
  private errorHandler = inject(ErrorHandler);
  private accountService = inject(AccountService);

  constructor() {}

  getConfig(relatedToType: TaskRelatedToModule): TsPageSidenavConfig {
    let actions = [];
    let canCreate = false;
    let canEdit = false;

    const isNewTaskPermissionsOn = this.store.selectSnapshot(
      AccountState.isFeatureEnabled(FeatureFlag.NEW_TASK_PERMISSIONS),
    );
    const isAggregatedTasks = relatedToType === TaskRelatedToModule.AggregatedTasks;
    const isGlobal = relatedToType === TaskRelatedToModule.Account;

    if (!isGlobal) {
      canCreate = this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_CREATE));
      canEdit = this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_EDIT));
    }

    if (isGlobal) {
      canCreate = this.store.selectSnapshot(UserState.hasPermission(NewPermission.GLOBAL_TASK_LISTS_CREATE));
      canEdit = this.store.selectSnapshot(UserState.hasPermission(NewPermission.GLOBAL_TASK_LISTS_EDIT));
    }

    if (isNewTaskPermissionsOn) {
      const createPermission = isAggregatedTasks
        ? NewPermission.AGGREGATED_TASK_LISTS_CREATE
        : NewPermission.GLOBAL_TASK_LISTS_CREATE;

      const currentTaskView = this.store.selectSnapshot(TaskState.getTaskView);
      canCreate = this.store.selectSnapshot(UserState.hasPermission(createPermission));
      canEdit = PermissionUtils.canEdit(currentTaskView);
    }

    if (canCreate) {
      actions.push({
        icon: 'add_circle_outline',
        description: 'Create new',
        actionFn: () => this.openCreateTaskListDialog(relatedToType),
      });
    }

    if (canEdit) {
      const isAggregatedTasks = relatedToType === TaskRelatedToModule.AggregatedTasks;
      const isOrderingEnabled =
        !isAggregatedTasks ||
        this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.AGGREGATED_TASKS_SAVED_VIEWS_ORDERING));

      if (isOrderingEnabled) {
        actions.push({
          icon: 'ts_cog',
          description: isAggregatedTasks ? 'Manage saved views' : 'Manage task lists',
          actionFn: () => this.navigateToSettings(relatedToType),
        });
      }
    }

    return {
      title: '',
      actions,
    };
  }

  navigateToSettings(relatedToType: TaskRelatedToModule): void {
    const currentUrl = this.router.url.split('?')[0].split('#')[0];
    const segments = currentUrl.split('/').filter(Boolean);
    const idx = segments.lastIndexOf(relatedToType);
    if (idx < 0 || idx + 1 >= segments.length) {
      return;
    }
    const settingsUrl = '/' + segments.slice(0, idx + 2).join('/') + '/' + TaskViewKind.TaskList + '/-1/settings';
    void this.router.navigateByUrl(settingsUrl);
  }

  openCreateTaskListDialog(relatedToType: TaskRelatedToModule) {
    const isAggregatedTasks = relatedToType === TaskRelatedToModule.AggregatedTasks;

    if (isAggregatedTasks) {
      this.createSavedView({ related_to_type: RelatedToType.AggregatedTasks })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError((error) => {
            this.errorHandler.handleError(error);
            return EMPTY;
          }),
        )
        .subscribe();
    } else {
      let applicableType = relatedToType as string;
      if (applicableType === TaskRelatedToModule.CustomObjectValue) {
        applicableType = this.store.selectSnapshot(JupiterState.getRelatedToObject).name;
      }

      this.matDialog.open(CreateTaskListComponent, { data: { applicableType } });
    }
  }

  /**
   * The single "create saved view" flow (sidebar "+" and the shell's "Save as"). `SavedViewAddComponent` asks for the
   * name and creates the view (root `SavedViewsActions.AddView`) from `params`; then `completeCreatedSavedView`. Emits
   * the new tasks-list view, or `null` on cancel. Errors after the dialog closes propagate to the caller.
   */
  createSavedView(params: SavedViewCreateParams): Observable<TaskView | null> {
    const dialogData = {
      relatedToType: params.related_to_type,
      definition: params.definition,
      visibility: params.visibility,
      saved_views: this.getSavedViews(params.related_to_type),
    };

    return this.matDialog
      .open(SavedViewAddComponent, { panelClass: 'mailbox-compose-dialog', data: dialogData })
      .afterClosed()
      .pipe(
        exhaustMap((result: { name?: string } | boolean | undefined) => {
          const name = typeof result === 'object' ? result?.name : undefined;
          if (!name) {
            return of(null);
          }
          return this.completeCreatedSavedView(params, name);
        }),
      );
  }

  /**
   * After the dialog created the view: saves `params.filters` on its backend-created saved filter (when given),
   * refreshes the tasks list of its type and emits the new view from that list (`null` if not found).
   */
  private completeCreatedSavedView(params: SavedViewCreateParams, name: string): Observable<TaskView | null> {
    const { related_to_type, filters } = params;
    const refreshAction =
      related_to_type === RelatedToType.AggregatedTasks
        ? new TaskActions.GetSidebarItems()
        : new TaskActions.LoadRecordTaskSavedViews({ force: true });
    const isNewView = (view: SavedView) => view.name === name && view.related_to_type === related_to_type;
    const savedViews = this.store.selectSnapshot(SavedViewsState.getViews) ?? [];
    const createdSavedView = savedViews.find(isNewView);
    // The backend creates the saved filter empty; the given filters are saved onto it.
    const savedFilter = { ...createdSavedView?.saved_filter, filters };
    const saveFilters$ = filters ? this.accountService.updateFilter(savedFilter) : of(null);

    return saveFilters$.pipe(
      switchMap(() => this.store.dispatch(refreshAction)),
      map(() => {
        const taskViews = this.getSavedViews(related_to_type);
        return taskViews.find((taskView) => taskView.name === name) ?? null;
      }),
    );
  }

  /** Saved views of `related_to_type` as held in state; names are unique per account and type. */
  private getSavedViews(relatedToType: SavedViewCreateParams['related_to_type']): TaskView[] {
    if (relatedToType === RelatedToType.RecordTasks) {
      return this.store.selectSnapshot(TaskState.getRecordTaskSavedViews) ?? [];
    }
    const sidebarItems = this.store.selectSnapshot(TaskState.getSidebarItems) ?? [];
    return sidebarItems.filter((item) => item?.type === 'SavedView');
  }
}
